// Updated ProgressDashboardComponent with tooltip functionality

import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { AppSettings } from "../../../app-settings";
import { StreakMessageService } from "app/Services/streak-message.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

interface WeekDay {
  date: string;
  day: number;
  percentage: number;
  status: string;
  dayStatus: string;
  isNoStudyDay: boolean;
  circleType: "striped" | "progress";
  iconUrl: string;
  circleColor: string;
  circleBgColor: string;
  showEndIcon: boolean;
  endIconUrl?: string;
  dayName: string;
  isCurrentDay: boolean;
  tooltipHtmlShown: boolean;
  didNotStudy: boolean;
}

@Component({
  selector: "app-progress-dashboard",
  templateUrl: "./progress-dashboard.component.html",
  styleUrls: ["./progress-dashboard.component.scss"],
})
export class ProgressDashboardComponent implements OnChanges {
  // Constants
  private readonly DAY_NAMES = [
    "SUN",
    "MON",
    "TUE",
    "WED",
    "THU",
    "FRI",
    "SAT",
  ];

  // CDN asset paths
  readonly consistencyStartIcon =
    AppSettings.cdnUrl + "assets/images/ico-calendar-black.svg";
  readonly consistencyIcon =
    AppSettings.cdnUrl + "assets/images/ico-calendar.svg";
  readonly diligenceStartIcon =
    AppSettings.cdnUrl + "assets/images/ico-book-black.svg";
  readonly diligenceIcon = AppSettings.cdnUrl + "assets/images/ico-book.svg";
  readonly IconStarUrl = AppSettings.cdnUrl + "assets/images/ico-star.svg";
  readonly startIcon = AppSettings.cdnUrl + "assets/images/ico-star-circle.svg";
  readonly infoIcon = AppSettings.cdnUrl + "assets/images/ico-info.svg";
  readonly noStudyBoostIcon =
    AppSettings.cdnUrl + "assets/images/no-study-boost.png";

  readonly headerImageUrl = AppSettings.cdnUrl + "assets/images/ico-header-popup.png";
  readonly bodyIconUrl = AppSettings.cdnUrl + "assets/images/ico-popup-boost.png";

  // Status values
  consistencyPercentage = 0;
  diligencePercentage = 0;
  goalCompletionRate = 0;

  // Tooltip states
  showConsistencyTooltip = false;
  showDiligenceTooltip = false;

  // Tooltip texts
  readonly consistencyTooltipText =
    "Consistency tracks your study routine by measuring how many days you showed up to study out of the total days in the current week. It answers the question: 'Am I building a regular study habit?' A higher percentage means you're establishing a more consistent routine, which is the foundation of effective learning.";
  readonly diligenceTooltipText =
    "Diligence measures how effectively you use your committed study time on active days. It calculates the percentage of your planned study hours that you actually completed. This metric reflects your focus and commitment to following through on your study goals. Improving this percentage helps maximize the return on the time you've set aside for learning.";

  // Status icons mapping - these are the actual SVG paths for each status
  readonly statusIcons = {
    FUTURE_DAY: "", // Future day has NO icon
    NO_STUDY_DAY: AppSettings.cdnUrl + "assets/images/ico-no study.svg",
    DID_NOT_STUDY:
      AppSettings.cdnUrl + "assets/images/ico-warning-did-not-study.svg",
    DID_NOT_REACH_GOAL: AppSettings.cdnUrl + "assets/images/ico-warning.svg",
    MET_GOAL: AppSettings.cdnUrl + "assets/images/ico-fire.svg",
    EXCEEDED_GOAL: AppSettings.cdnUrl + "assets/images/ico-fire.svg",
    IN_PROGRESS: AppSettings.cdnUrl + "assets/images/ico-fire-gray.svg",
  };

  @Input() data;

  showPopup: boolean = false;

  weekDays: WeekDay[] = [];

  showNoStudyDayBox: boolean = false;

  tooltipHtml: string = '';

  OverCommitmentMessage = '';

  constructor(
    private streakService: StreakMessageService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data?.progressStreak?.weeklyProgress) {
      this.initializeWeekData();
    }
  }

  /**
   * Calculate total time spent across all stages (ES5 compatible)
   */
  private calculateTotalTimeSpent(timeSpentByStage: any): number {
    if (!timeSpentByStage) return 0;

    let total = 0;

    // ES5 compatible iteration through object keys
    Object.keys(timeSpentByStage).forEach((stageKey) => {
      const stage = timeSpentByStage[stageKey];

      if (stage) {
        Object.keys(stage).forEach((activityKey) => {
          const time = stage[activityKey];
          if (typeof time === "number") {
            total += time;
          }
        });
      }
    });

    return total;
  }

  messageData = {
    iconUrl: "",
    heading: "",
    message: "",
  };

  goToPspPlan() {
    window.open(AppSettings.gfePSPUrl, "_blank");
  }

  navigateToCalendar(){
    window.open(AppSettings.gfePSPUrl, '_blank');
  }

  openPopup() {
    this.showPopup = true;
  }

  /**
   * Initialize weekly data and pre-calculate all UI configurations
   */
  private initializeWeekData(): void {this.showNoStudyDayBox = false;
    this.consistencyPercentage = this.data?.progressStreak?.consistency;
    this.diligencePercentage = this.data?.progressStreak?.diligence;
    this.goalCompletionRate = this.data?.progressStreak?.goalCompletionRate;
    // Create an array to store processed week days
    const processedWeekDays: WeekDay[] = [];
  
    // Define minimum study time threshold in seconds (5 minutes = 300 seconds)
    const MIN_STUDY_TIME_THRESHOLD = 300;
  
    // Process each day in the weekly progress data
    this.data.progressStreak.weeklyProgress.forEach((weekData, i) => {
      const date = new Date(weekData.date);
      const day = date.getDay();
  
      // Get time spent data for this day
      const timeSpentData = this.data.userTimeSpent.timeSpentByPeriod[
        weekData.date
      ]
        ? this.data.userTimeSpent.timeSpentByPeriod[weekData.date]
        : { timeSpentByStage: {} };
      const totalTimeSpent = this.calculateTotalTimeSpent(
        timeSpentData?.timeSpentByStage
      );
  
      // Get committed time for this day
      const committedTime = this.data.committedTimeMap[weekData.date] || 0;
      const isNoStudyDay = weekData.noStudyDate === true;
  
      let status = "";
      let iconUrl = "";
      let circleColor = "";
      let circleBgColor = "";
      let circleType: "striped" | "progress" = "progress";
      let showEndIcon = false;
  
      let didNotStudy = false;
  
      // Apply the 5-minute threshold logic
      const dayStatus = weekData.dayStatus;
      // Create a local variable that we can modify instead of trying to modify the input data
      let effectiveStudyStatus;
if (dayStatus === "CURRENT_DAY" && !isNoStudyDay) {
  // For current day, maintain original status or use IN_PROGRESS
  effectiveStudyStatus = "DID_NOT_REACH_GOAL";
} else {
  // For past days, apply the threshold logic
  effectiveStudyStatus = totalTimeSpent < MIN_STUDY_TIME_THRESHOLD && !isNoStudyDay 
    ? "DID_NOT_STUDY" 
    : weekData.studyStatus;
}
      // If we've determined this is effectively a "did not study" day
      if (effectiveStudyStatus === "DID_NOT_STUDY" && dayStatus == "PAST_DAY") {
        didNotStudy = true;
      }
  
      let tooltipHtmlShown = false;
      if (this.data?.weekType === "current") {
        if (dayStatus === "CURRENT_DAY") {
          this.messageData = this.streakService.getStreakMessage(
            this.consistencyPercentage,
            this.diligencePercentage,
            this.goalCompletionRate,
            i + 1
          );
        }
      } else {
        if (i === 0) {
          if(this.consistencyPercentage == 100 && this.diligencePercentage < 65 && this.data.progressStreak.committedHours > 90000){
            this.messageData.heading = 'Overcommitment';
            this.messageData.message = 'You haven\'t reached your goals because <b>you\'ve likely committed to too many study hours</b>. Set more realistic targets: 20-25 hours weekly if you are working full-time, or about 45 hours if you are preparing for the GMAT full-time. Update your study plan with goals you can actually achieve, make sure your good attendance is matched with enough quality study time.<br><br>If indeed your goals are realistic, then next week, focus on matching your perfect attendance with adequate, well-directed study hours.';
          }
          else{
            this.messageData = this.streakService.getWeeklySummaryMessage(
              this.consistencyPercentage,
              this.diligencePercentage,
              this.goalCompletionRate
            );
          }
        }
      }
  
      if (isNoStudyDay) {
        status = "NO_STUDY_DAY";
        iconUrl = this.statusIcons["NO_STUDY_DAY"];
        circleType = "striped";
        circleColor = "#D2D2D2";
        circleBgColor = "#F4F4F4";
        if (totalTimeSpent >= 180) {
          switch (dayStatus) {
            case "CURRENT_DAY":
              switch (effectiveStudyStatus) {
                case "EXCEEDED_GOAL":
                case "MET_GOAL":
                case "DID_NOT_REACH_GOAL":
                  // tooltipHtmlShown = true;
                  this.openPopup();
                  break;
              }
              break;
            case "PAST_DAY":
              switch (effectiveStudyStatus) {
                case "EXCEEDED_GOAL":
                case "MET_GOAL":
                case "DID_NOT_REACH_GOAL":
                  tooltipHtmlShown = true;
                  this.tooltipHtml = `We noticed that you utilized your <b>'No Study Day'</b>—great dedication! If you'd like this session to be tracked in your progress for the week, update your schedule by switching this No Study Day to a Study Day in your calendar. <a target="_blank" href="${AppSettings.gfePSPUrl}">Go to Calendar</a>`;
                  this.showNoStudyDayBox = true;
                  break;
              }
              break;
          }
        }
      } else {
        status = effectiveStudyStatus;
        if (status === "EXCEEDED_GOAL") {
          showEndIcon = true;
        }
        
        // Apply visual styles based on the (potentially modified) study status
        switch (dayStatus) {
          case "CURRENT_DAY":
            switch (effectiveStudyStatus) {
              case "DID_NOT_STUDY":
                iconUrl = this.statusIcons["DID_NOT_STUDY"];
                circleType = "progress";
                circleColor = "#D2D2D2"; // gray for did not study
                circleBgColor = "#F4F4F4";
                didNotStudy = true;
                break;
              case "DID_NOT_REACH_GOAL":
                iconUrl = this.statusIcons["IN_PROGRESS"];
                circleType = "progress";
                circleColor = "#4AFF52"; // green dark
                circleBgColor = "#D2FFD4"; // green light
                break;
              case "MET_GOAL":
              case "EXCEEDED_GOAL":
                iconUrl = this.statusIcons[effectiveStudyStatus];
                circleType = "progress";
                circleColor = "#4AFF52"; // green dark
                circleBgColor = "#D2FFD4"; // green light
                break;
            }
            break;
          case "PAST_DAY":
            iconUrl = this.statusIcons[effectiveStudyStatus];
            circleType = "progress";
            circleColor = "#4AFF52"; // green dark
            circleBgColor = "#D2FFD4"; // green light
            if (effectiveStudyStatus === "DID_NOT_STUDY") {
              circleColor = "#D2D2D2";
              circleBgColor = "#F4F4F4";
              didNotStudy = true;
            }
            break;
          case "FUTURE_DAY":
            iconUrl = this.statusIcons["FUTURE_DAY"];
            circleType = "progress";
            circleColor = "#7F7F7F";
            circleBgColor = "#D2D2D2";
            break;
        }
      }
  
      // Calculate percentage of goal achieved
      // If below threshold, show 0% even if there was minimal activity
      const percentage = (!isNoStudyDay && totalTimeSpent >= MIN_STUDY_TIME_THRESHOLD)
        ? Math.round((totalTimeSpent / committedTime) * 100)
        : 0;
        
      // Create a weekDay object with all the necessary properties
      const weekDay: WeekDay = {
        date: weekData.date,
        day,
        percentage,
        status,
        dayStatus: weekData.dayStatus,
        isNoStudyDay,
        // Pre-computed UI properties based on the corrected logic
        circleType,
        iconUrl,
        circleColor,
        circleBgColor,
        showEndIcon,
        endIconUrl: showEndIcon ? this.startIcon : undefined,
        dayName: this.DAY_NAMES[day],
        isCurrentDay: weekData.dayStatus === "CURRENT_DAY",
        tooltipHtmlShown: tooltipHtmlShown,
        didNotStudy: didNotStudy,
      };
  
      // Add the weekDay to our array
      processedWeekDays.push(weekDay);
    });
  
    // Update the weekDays array with our processed data
    this.weekDays = processedWeekDays;

    // Debug log to verify the status of each day
  }

  // Toggle tooltip visibility methods
  toggleConsistencyTooltip(flag, event): void {
    event.stopPropagation();
    this.showConsistencyTooltip = flag;
  }

  toggleDiligenceTooltip(flag, event): void {
    event.stopPropagation();
    this.showDiligenceTooltip = flag;
  }
}
